// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, Edit2, Save, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface EventFAQ {
  id: string;
  question: string;
  answer: string;
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  event_id?: string;
}

interface FAQsTabProps {
  eventId?: string;
}

const FAQsTab: React.FC<FAQsTabProps> = ({ eventId }) => {
  const [faqs, setFaqs] = useState<EventFAQ[]>([]);
  const [globalFaqs, setGlobalFaqs] = useState<EventFAQ[]>([]);
  const [linkedFaqIds, setLinkedFaqIds] = useState<Set<string>>(new Set());
  const [selectedFaqIds, setSelectedFaqIds] = useState<Set<string>>(new Set());
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingFaq, setEditingFaq] = useState({ question: '', answer: '', category: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log('📘 FAQsTab useEffect triggered, eventId:', eventId);
    // Always fetch global FAQs, regardless of eventId
    fetchGlobalFAQs();
    
    // Only fetch event-specific FAQs if eventId exists
    if (eventId) {
      console.log('📘 EventId exists, fetching event FAQs...');
      fetchEventFAQs();
    } else {
      console.log('📘 No eventId provided, only fetching global FAQs');
    }
  }, [eventId]);

  const fetchEventFAQs = async () => {
    console.log('📘 fetchEventFAQs called with eventId:', eventId);
    if (!eventId) {
      console.log('📘 No eventId, returning early');
      return;
    }

    setLoading(true);
    try {
      // Get FAQs linked to this event through the junction table
      const { data: linkedFaqs, error: linkError } = await supabase
        .from('event_faq_links')
        .select(`
          faq_id,
          event_faqs (*)
        `)
        .eq('event_id', eventId);

      if (linkError) throw linkError;

      // Get direct FAQs for this event (legacy support)
      const { data: directFaqs, error: directError } = await supabase
        .from('event_faqs')
        .select('*')
        .eq('event_id', eventId);

      if (directError) throw directError;

      // Merge the FAQs properly
      const linkedFaqData = linkedFaqs?.map(link => link.event_faqs).filter(Boolean) || [];
      const mergedFaqs = [...(directFaqs || []), ...linkedFaqData];
      console.log('📘 Merged event FAQs:', mergedFaqs);
      setFaqs(mergedFaqs);

      // Track which FAQs are linked
      const linkedIds = new Set(linkedFaqs?.map(link => link.faq_id) || []);
      console.log('📘 Linked FAQ IDs:', Array.from(linkedIds));
      setLinkedFaqIds(linkedIds);

    } catch (error) {
      console.error('Error fetching event FAQs:', error);
      toast.error('Failed to load event FAQs');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalFAQs = async () => {
    try {
      console.log('📘 Fetching global FAQs...');
      const { data, error } = await supabase
        .from('event_faqs')
        .select('*')
        .is('event_id', null)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('📘 Error in fetchGlobalFAQs:', error);
        throw error;
      }
      
      console.log('📘 Global FAQs data:', data);
      console.log('📘 Global FAQs count:', data?.length || 0);
      setGlobalFaqs(data || []);
      console.log('📘 State updated, globalFaqs should now have:', data?.length || 0, 'items');
    } catch (error) {
      console.error('Error fetching global FAQs:', error);
      toast.error('Failed to load global FAQs');
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    try {
      // Create a global FAQ (event_id = null)
      const { data: faqData, error: faqError } = await supabase
        .from('event_faqs')
        .insert({
          question: newFaq.question.trim(),
          answer: newFaq.answer.trim(),
          category: newFaq.category.trim() || null,
          is_active: true,
          event_id: null // Make it global
        })
        .select()
        .single();

      if (faqError) throw faqError;

      if (eventId && faqData) {
        // Link the FAQ to the current event
        const { error: linkError } = await supabase
          .from('event_faq_links')
          .insert({
            event_id: eventId,
            faq_id: faqData.id
          });

        if (linkError) throw linkError;

        // Update linked FAQ IDs
        setLinkedFaqIds(prev => new Set([...prev, faqData.id]));
      }

      // Reset form and refresh data
      setNewFaq({ question: '', answer: '', category: '' });
      await fetchEventFAQs();
      await fetchGlobalFAQs();
      toast.success('FAQ created and linked successfully');
    } catch (error) {
      console.error('Error adding FAQ:', error);
      toast.error('Failed to add FAQ');
    }
  };

  const handleEditFaq = (faq: EventFAQ) => {
    setEditingId(faq.id);
    setEditingFaq({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingFaq.question.trim() || !editingFaq.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    try {
      const { error } = await supabase
        .from('event_faqs')
        .update({
          question: editingFaq.question.trim(),
          answer: editingFaq.answer.trim(),
          category: editingFaq.category.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingId);

      if (error) throw error;

      setEditingId(null);
      setEditingFaq({ question: '', answer: '', category: '' });
      await fetchEventFAQs();
      await fetchGlobalFAQs();
      toast.success('FAQ updated successfully');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error('Failed to update FAQ');
    }
  };

  const handleDeleteFaq = async (faqId: string) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      // If it's linked to this event, unlink it first
      if (eventId && linkedFaqIds.has(faqId)) {
        const { error: unlinkError } = await supabase
          .from('event_faq_links')
          .delete()
          .eq('event_id', eventId)
          .eq('faq_id', faqId);

        if (unlinkError) throw unlinkError;
      }

      // Delete the FAQ itself
      const { error } = await supabase
        .from('event_faqs')
        .delete()
        .eq('id', faqId);

      if (error) throw error;

      await fetchEventFAQs();
      await fetchGlobalFAQs();
      toast.success('FAQ deleted successfully');
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const toggleSelection = (faqId: string) => {
    // If FAQ is already linked, unlink it instead of selecting
    if (linkedFaqIds.has(faqId)) {
      handleToggleLink(faqId, false);
      return;
    }
    
    setSelectedFaqIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(faqId)) {
        newSet.delete(faqId);
      } else {
        newSet.add(faqId);
      }
      return newSet;
    });
  };

  const handleLinkSelectedFAQs = async () => {
    if (!eventId || selectedFaqIds.size === 0) return;

    try {
      // Filter out FAQs that are already linked
      const faqsToLink = Array.from(selectedFaqIds).filter(faqId => !linkedFaqIds.has(faqId));
      
      if (faqsToLink.length === 0) {
        toast.error('All selected FAQs are already linked to this event');
        return;
      }

      // Bulk insert the links
      const linksToInsert = faqsToLink.map(faqId => ({
        event_id: eventId,
        faq_id: faqId
      }));

      const { error } = await supabase
        .from('event_faq_links')
        .insert(linksToInsert);

      if (error) throw error;

      // Update linked FAQ IDs
      setLinkedFaqIds(prev => new Set([...prev, ...faqsToLink]));
      
      // Clear selections
      setSelectedFaqIds(new Set());
      
      await fetchEventFAQs();
      toast.success(`${faqsToLink.length} FAQ(s) linked to event successfully`);
    } catch (error) {
      console.error('Error linking selected FAQs:', error);
      toast.error('Failed to link selected FAQs');
    }
  };

  const handleToggleLink = async (faqId: string, isLinked: boolean) => {
    if (!eventId) return;

    try {
      if (isLinked) {
        // Unlink FAQ from event
        const { error } = await supabase
          .from('event_faq_links')
          .delete()
          .eq('event_id', eventId)
          .eq('faq_id', faqId);

        if (error) throw error;
        setLinkedFaqIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(faqId);
          return newSet;
        });
        toast.success('FAQ unlinked from event');
      } else {
        // Link FAQ to event
        const { error } = await supabase
          .from('event_faq_links')
          .insert({
            event_id: eventId,
            faq_id: faqId
          });

        if (error) throw error;
        setLinkedFaqIds(prev => new Set([...prev, faqId]));
        toast.success('FAQ linked to event');
      }

      await fetchEventFAQs();
    } catch (error) {
      console.error('Error toggling FAQ link:', error);
      toast.error('Failed to update FAQ link');
    }
  };

  const renderFaqItem = (faq: EventFAQ, showLinkToggle = false) => (
    <Card key={faq.id} className="mb-4">
      <CardContent className="pt-4">
        {editingId === faq.id ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor={`edit-question-${faq.id}`}>Question</Label>
              <Input
                id={`edit-question-${faq.id}`}
                value={editingFaq.question}
                onChange={(e) => setEditingFaq(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter question"
              />
            </div>
            <div>
              <Label htmlFor={`edit-answer-${faq.id}`}>Answer</Label>
              <Textarea
                id={`edit-answer-${faq.id}`}
                value={editingFaq.answer}
                onChange={(e) => setEditingFaq(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Enter answer"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor={`edit-category-${faq.id}`}>Category (Optional)</Label>
              <Input
                id={`edit-category-${faq.id}`}
                value={editingFaq.category}
                onChange={(e) => setEditingFaq(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Enter category"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} size="sm">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setEditingId(null)} 
                size="sm"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-medium text-sm mb-1">{faq.question}</h4>
                <p className="text-sm text-gray-600 mb-2">{faq.answer}</p>
                {faq.category && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {faq.category}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                {showLinkToggle && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`select-${faq.id}`}
                      checked={linkedFaqIds.has(faq.id) || selectedFaqIds.has(faq.id)}
                      onCheckedChange={() => toggleSelection(faq.id)}
                    />
                    <Label htmlFor={`select-${faq.id}`} className="text-sm">
                      {linkedFaqIds.has(faq.id) ? 'Linked (click to unlink)' : 'Select'}
                    </Label>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditFaq(faq)}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteFaq(faq.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="p-4">Loading FAQs...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="event-faqs" className="w-full">
        <TabsList>
          <TabsTrigger value="event-faqs">Event FAQs</TabsTrigger>
          <TabsTrigger value="all-faqs">All FAQs</TabsTrigger>
        </TabsList>

        <TabsContent value="event-faqs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Add New FAQ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="new-question">Question</Label>
                <Input
                  id="new-question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Enter question"
                />
              </div>
              <div>
                <Label htmlFor="new-answer">Answer</Label>
                <Textarea
                  id="new-answer"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Enter answer"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="new-category">Category (Optional)</Label>
                <Input
                  id="new-category"
                  value={newFaq.category}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Enter category"
                />
              </div>
              <Button onClick={handleAddFaq}>
                <Plus className="h-4 w-4 mr-2" />
                Add FAQ
              </Button>
            </CardContent>
          </Card>

          <div>
            <h3 className="text-lg font-medium mb-4">Event FAQs ({faqs.length})</h3>
            {faqs.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-gray-500">No FAQs linked to this event yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div>
                {faqs.map(faq => renderFaqItem(faq))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all-faqs" className="space-y-4">
          <div>
            <h3 className="text-lg font-medium mb-4">All Global FAQs ({globalFaqs.length})</h3>
            <p className="text-sm text-gray-600 mb-4">
              Check the box next to FAQs you want to link to this event.
            </p>
            
            {selectedFaqIds.size > 0 && eventId && (
              <div className="mb-4">
                <Button 
                  onClick={handleLinkSelectedFAQs}
                  disabled={selectedFaqIds.size === 0}
                  className="mb-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Link Selected FAQs ({selectedFaqIds.size})
                </Button>
              </div>
            )}
            
            {(() => {
              console.log('📘 Rendering All FAQs tab, globalFaqs.length:', globalFaqs.length);
              console.log('📘 Current globalFaqs state:', globalFaqs);
              return null;
            })()}
            {globalFaqs.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-gray-500">No global FAQs available.</p>
                </CardContent>
              </Card>
            ) : (
              <div>
                {globalFaqs.map(faq => renderFaqItem(faq, true))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FAQsTab;
